import { useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { useFormik } from 'formik'
import { useUserStore } from 'Store/User'
import { useRoleStore } from 'Store/Roles'
import { UserListViewContext } from '../List/Context'
import { UserFormModalContext } from './Context'
import * as Yup from 'yup'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'

const INITIAL_VALUE = {
  username: '',
  employeeCode: '',
  fullName: '',
  roleId: '',
  password: '',
}

const VALIDATION_SCHEMA = Yup.object().shape({
  username: Yup.string()
    .matches(/^[A-Za-z0-9.]+$/, 'Username อนุญาตเฉพาะ A-Z, a-z และตัวเลข เท่านั้น')
    .required('กรุณากรอกชื่อผู้ใช้งาน'),
  fullName: Yup.string().required('กรุณากรอกชื่อ-นามสกุล'),
  roleId: Yup.number().required('กรุณาเลือกตำแหน่ง'),
  password: Yup.string().min(6, 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร'),
})

const ViewModel = () => {
  const { isOpenUserModal, toggleUserModal } = useContext(UserListViewContext)
  const { formType } = useContext(UserFormModalContext)
  const {
    getAll,
    getOne,
    selected,
    setSelected,
    create,
    update,
    uploadSignature,
    getSignature,
    deleteSignature,
  } = useUserStore(
    state => ({
      getAll: state.getAll,
      getOne: state.getOne,
      selected: state.selected,
      setSelected: state.setSelected,
      create: state.create,
      update: state.edit,
      uploadSignature: state.uploadSignature,
      getSignature: state.getSignature,
      deleteSignature: state.deleteSignature,
    }),
    shallow
  )
  const { roles, getRoles, clearStateRoles } = useRoleStore(state => ({
    roles: state.data,
    getRoles: state.getAll,
    clearStateRoles: state.clearState,
  }))
  const [isLoadingRoles, setIsLoadingRoles] = useState(false)
  const [isSignatureUploading, setIsSignatureUploading] = useState(false)
  const [isSignatureDeleting, setIsSignatureDeleting] = useState(false)
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState<string | null>(null)

  const roleOptions = useMemo(
    () =>
      roles.map((r: any) => ({
        label: r.title,
        value: r.id,
      })),
    [roles]
  )

  const clearSignaturePreview = useCallback(() => {
    if (signaturePreviewUrl) {
      URL.revokeObjectURL(signaturePreviewUrl)
    }
    setSignaturePreviewUrl(null)
  }, [signaturePreviewUrl])

  const loadSignaturePreview = useCallback(
    async (userId: string) => {
      clearSignaturePreview()
      const { success, data } = await getSignature(userId)
      if (success && typeof data === 'string') {
        setSignaturePreviewUrl(data)
      }
    },
    [clearSignaturePreview, getSignature]
  )

  const refreshSelectedUser = useCallback(
    async (userId: string) => {
      const { success, data } = await getOne(userId)
      if (success && data) {
        setSelected(data)
        if (data.hasSignature) {
          await loadSignaturePreview(userId)
        } else {
          clearSignaturePreview()
        }
      }
    },
    [clearSignaturePreview, getOne, loadSignaturePreview, setSelected]
  )

  const submit = async ({
    username,
    employeeCode,
    fullName,
    roleId,
    password,
  }: {
    username: string
    employeeCode: string
    fullName: string
    roleId: string
    password: string
  }) => {
    try {
      if (formType === 'add') {
        const { success, data } = await create({ username, employeeCode, fullName, roleId, password })
        if (!success) {
          console.log('error', data)
          toast.error(data)
          return false
        } else {
          toast.success('เพิ่มผู้ใช้งานสำเร็จ')
          await getAll()
          toggleUserModal(false)
          return true
        }
      } else if (formType === 'edit') {
        const { success, data } = await update(selected?.id, {
          username,
          employeeCode,
          fullName,
          roleId,
          password,
        })

        if (!success) {
          toast.error(data)
          return false
        } else {
          toast.success('แก้ไขผู้ใช้งานสำเร็จ')
          await getAll()
          toggleUserModal(false)
          return true
        }
      }
    } catch (err: any) {
      console.log(err)
      toast.error(err?.message)
      return false
    }
  }

  const handleSignatureUpload = async (file: File) => {
    if (!selected?.id) return
    const userId = String(selected.id)
    setIsSignatureUploading(true)
    try {
      const { success, data } = await uploadSignature(userId, file)
      if (success) {
        toast.success('อัปโหลดลายเซ็นสำเร็จ')
        setSelected({ ...selected, ...data, hasSignature: true })
        await loadSignaturePreview(userId)
      } else {
        toast.error(data ?? 'อัปโหลดลายเซ็นไม่สำเร็จ')
      }
    } finally {
      setIsSignatureUploading(false)
    }
  }

  const handleSignatureDelete = async () => {
    if (!selected?.id) return
    const userId = String(selected.id)
    const { isConfirmed } = await Swal.fire({
      title: 'ลบลายเซ็น?',
      text: 'ลายเซ็นที่ลบแล้วจะไม่สามารถกู้คืนได้',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    })
    if (!isConfirmed) return

    setIsSignatureDeleting(true)
    try {
      const { success, data } = await deleteSignature(userId)
      if (success) {
        toast.success('ลบลายเซ็นสำเร็จ')
        clearSignaturePreview()
        await refreshSelectedUser(userId)
      } else {
        toast.error(data ?? 'ลบลายเซ็นไม่สำเร็จ')
      }
    } finally {
      setIsSignatureDeleting(false)
    }
  }

  const fetchData = () => {
    if (isOpenUserModal) {
      setIsLoadingRoles(true)
      getRoles().then(() => {
        setIsLoadingRoles(false)
      })
    }
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: INITIAL_VALUE,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: async (values, { resetForm }) => {
      const status = await submit(values)
      if (status) resetForm()
    },
  })

  const handleClose = () => {
    clearStateRoles()
    clearSignaturePreview()
    toggleUserModal(false)
    formik.resetForm()
  }

  const modalsRoot = document.getElementById('root-modals') || document.body

  useEffect(() => {
    if (formType === 'edit' && selected) {
      formik.setValues({
        username: selected.username,
        employeeCode: selected.employeeCode ?? '',
        fullName: selected.fullName,
        roleId: selected.roleId,
        password: '',
      })
      if (selected.id) {
        refreshSelectedUser(String(selected.id))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType, selected?.id])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenUserModal])

  useEffect(() => {
    return () => {
      if (signaturePreviewUrl) {
        URL.revokeObjectURL(signaturePreviewUrl)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    modalsRoot,
    formType,
    isLoadingRoles,
    isOpenUserModal,
    roleOptions,
    handleClose,
    formik,
    selected,
    signaturePreviewUrl,
    isSignatureUploading,
    isSignatureDeleting,
    handleSignatureUpload,
    handleSignatureDelete,
  }
}

export default ViewModel
